import React from 'react';
import { MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDateTimeDisplay } from '../../lib/datetime';

const SupportTab = ({
  tickets,
  appTimeZone,
  handleUpdateTicket,
  mobile = false,
}) => {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Support Tickets
        </CardTitle>
        <CardDescription>Escalated conversations requiring human response</CardDescription>
      </CardHeader>
      <CardContent>
        {tickets.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No support tickets yet</p>
          </div>
        ) : mobile ? (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-white">{ticket.subject}</div>
                    <div className="truncate text-sm text-zinc-500">{ticket.user_email}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant={
                      ticket.priority === 'high' ? 'destructive' :
                      ticket.priority === 'medium' ? 'default' : 'secondary'
                    }>
                      {ticket.priority}
                    </Badge>
                    <Badge variant={
                      ticket.status === 'open' ? 'outline' :
                      ticket.status === 'resolved' ? 'default' : 'secondary'
                    }>
                      {ticket.status}
                    </Badge>
                  </div>
                </div>
                <div className="mt-3 text-xs text-zinc-500">
                  {formatDateTimeDisplay(ticket.created_at, appTimeZone)}
                </div>
                <div className="mt-4">
                  <Select
                    value={ticket.status}
                    onValueChange={(value) => handleUpdateTicket(ticket.id, value)}
                  >
                    <SelectTrigger className="w-full bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead>Subject</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id} className="border-zinc-800">
                  <TableCell className="font-medium">{ticket.subject}</TableCell>
                  <TableCell className="text-zinc-500">{ticket.user_email}</TableCell>
                  <TableCell>
                    <Badge variant={
                      ticket.priority === 'high' ? 'destructive' :
                      ticket.priority === 'medium' ? 'default' : 'secondary'
                    }>
                      {ticket.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      ticket.status === 'open' ? 'outline' :
                      ticket.status === 'resolved' ? 'default' : 'secondary'
                    }>
                      {ticket.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-500 text-sm">
                    {formatDateTimeDisplay(ticket.created_at, appTimeZone)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Select
                      value={ticket.status}
                      onValueChange={(value) => handleUpdateTicket(ticket.id, value)}
                    >
                      <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default SupportTab;
